import { styled } from '@mui/system'
import { BORDER_RADIUSES, colors, fontSizes, zIndex } from '../../../Constants/consts'
import type { OpenProps } from '../navbar.types'

const mobileNavItemStyles = {
  display: 'flex',
  alignItems: 'center',
  color: colors.white,
  textDecoration: 'none',
  fontSize: fontSizes.semiLarge,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  transition: 'color 0.15s ease, padding-left 0.15s ease',
  '&:hover': {
    color: colors.yellow,
  },
}

export const NavActionCon = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: colors.yellow,
  borderRadius: BORDER_RADIUSES.small,
  padding: theme.spacing(0.75, 2),
  color: colors.black,
  textDecoration: 'none',
  fontSize: fontSizes.small,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  whiteSpace: 'nowrap',
  transition: 'opacity 0.15s ease',
  '&:hover': {
    opacity: 0.9,
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.white}`,
    outlineOffset: 2,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    fontSize: fontSizes.xSmall,
  },
}))

export const NavbarCon = styled('nav')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 50,
  background: colors.navPrimary,
  borderBottom: `1px solid ${colors.navBorder}`,
  position: 'sticky',
  top: 0,
  zIndex: zIndex.navbar,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    height: 45,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

export const SiteNavBrand = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  '& a img': {
    height: 35,
    width: 'auto',
    [theme.breakpoints.down('lg')]: {
      height: 28,
    },
  },
}))

export const DesktopNav = styled('ul')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}))

export const DesktopNavItem = styled('li')({
  position: 'relative',
})

export const SiteNavLink = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 50,
  padding: theme.spacing(0, 1.5),
  '& a, & > div': {
    display: 'flex',
    alignItems: 'center',
    color: colors.white,
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: fontSizes.medium,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    transition: 'color 0.15s ease',
  },
  '&:hover a, &:hover > div': {
    color: colors.yellow,
  },
  [theme.breakpoints.down('xl')]: {
    '& a, & > div': {
      fontSize: fontSizes.small,
    },
  },
}))

export const SiteNavButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  height: 50,
  padding: theme.spacing(0, 1.5),
  color: colors.white,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: fontSizes.medium,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  transition: 'color 0.15s ease',
  '&:hover': {
    color: colors.yellow,
  },
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.yellow}`,
    outlineOffset: -2,
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: fontSizes.small,
  },
}))

export const RightActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}))

export const MobileActions = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    marginLeft: 'auto',
  },
}))

export const ProfileIconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '& a': {
    display: 'flex',
    alignItems: 'center',
  },
  '& svg': {
    color: colors.white,
    fontSize: 32,
    transition: 'color 0.15s ease',
    '&:hover': {
      color: colors.yellow,
    },
  },
})

export const SubDropdown = styled('ul')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  minWidth: 200,
  background: colors.navDropdownBg,
  border: `1px solid ${colors.navBorder}`,
  borderRadius: BORDER_RADIUSES.small,
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  listStyle: 'none',
  padding: theme.spacing(0.5, 0),
  margin: 0,
  zIndex: zIndex.navDropdown,
  animation: 'dropdownFadeIn 0.15s ease',
  '@keyframes dropdownFadeIn': {
    from: { opacity: 0, transform: 'translateY(-8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '& li a, & li .default > button': {
    display: 'block',
    padding: theme.spacing(1, 2.5),
    color: colors.gray.gray20,
    textDecoration: 'none',
    fontSize: fontSizes.small,
    fontWeight: 500,
    lineHeight: 'normal',
    height: 'auto',
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    cursor: 'pointer',
    transition: 'color 0.12s ease, background-color 0.12s ease',
    '&:hover': {
      color: colors.yellow,
    },
  },
  [theme.breakpoints.down('lg')]: {
    position: 'static',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    background: 'transparent',
    animation: 'none',
  },
}))

export const DropdownChevron = styled('span')<OpenProps>(({ open }) => ({
  display: 'inline-block',
  fontSize: 10,
  transform: open ? 'rotate(180deg)' : 'rotate(0)',
  transition: 'transform 0.2s ease',
  '&::after': {
    content: '"▾"',
  },
}))

export const MobileNavPanel = styled('nav')<OpenProps>(({ open, theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '80vw',
  maxWidth: 320,
  height: '100dvh',
  background: colors.navPrimary,
  transform: open ? 'translateX(0)' : 'translateX(-100%)',
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: zIndex.mobilePanel,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}))

export const MobileNavScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none' as const,
})

export const MobileNavOverlay = styled('div')<OpenProps>(({ open, theme }) => ({
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  backdropFilter: 'blur(4px)',
  opacity: open ? 1 : 0,
  pointerEvents: open ? 'auto' : 'none',
  transition: 'opacity 0.3s ease',
  zIndex: zIndex.mobilePanel - 1,
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}))

export const MobilePanelHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  borderBottom: `1px solid ${colors.navBorder}`,
  '& img': {
    height: 32,
    width: 'auto',
  },
}))

export const MobileNavItem = styled('div')(({ theme }) => ({
  '& a, & > div': {
    ...mobileNavItemStyles,
    padding: theme.spacing(1.75, 3),
    borderBottom: `1px solid ${colors.navBorder}`,
    cursor: 'pointer',
    '&:hover': {
      ...mobileNavItemStyles['&:hover'],
      paddingLeft: theme.spacing(4),
    },
  },
}))

export const MobileSectionLabel = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 3, 0.5),
  fontSize: fontSizes.xSmall,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  color: colors.gray.gray60,
}))

export const MobileSubItem = styled('div')(({ theme }) => ({
  '& a': {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 3, 1.25, 4),
    color: colors.gray.gray40,
    textDecoration: 'none',
    fontSize: fontSizes.medium,
    borderBottom: `1px solid ${colors.navBorder}`,
    transition: 'color 0.12s ease',
    '&:hover': {
      color: colors.yellow,
    },
  },
}))

export const MobileCtaContainer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
  borderTop: `1px solid ${colors.navBorder}`,
  background: colors.navPrimary,
  '& a': {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
}))
